import React from 'react';
// Change the import to match the working example
import ReactDOM from 'react-dom/client';
import type { Root } from 'react-dom/client';
import LabelLayoutDialog from './LabelLayoutDialog';

// Define the types for the data being passed in
interface LabelProps
{
    labelWidth: number;
    labelHeight: number;
    labelTopMargin: number;
    labelLeftMargin: number;
    numOfCols: number;
    numOfRows: number;
    horizontalSpace: number;
    verticalSpace: number;
    units: string;
}

interface PageProperties
{
    x: number;
    y: number;
    width: number;
    height: number;
}

// Define the type for the configuration object passed to init()
interface LabelLayoutConfig
{
    labelProperties: Partial<LabelProps>;
    pageProperties: Partial<PageProperties>;
    onReturnLayout: (modifiedProps: Partial<LabelProps>) => void;
    onCancel?: () => void;
}

// Keep track of the React root and the DOM container
let root: Root | null = null;
let dialogContainer: HTMLDivElement | null = null;

// This is the public API that will be exposed on the window object
const LabelLayoutAPI = {
    /**
     * Initializes and renders the LabelLayoutDialog component.
     */
    init: ({ labelProperties, pageProperties, onReturnLayout, onCancel }: LabelLayoutConfig) =>
    {
        if (root)
        {
            LabelLayoutAPI.close();
        }

        dialogContainer = document.createElement('div');
        document.body.appendChild(dialogContainer);
        // Use ReactDOM.createRoot() to match the working example
        root = ReactDOM.createRoot(dialogContainer);

        const handleClose = () =>
        {
            if (onCancel)
            {
                onCancel();
            }
            LabelLayoutAPI.close();
        };

        const handleReturnLayout = (modifiedProps: Partial<LabelProps>) =>
        {
            onReturnLayout(modifiedProps);
            LabelLayoutAPI.close();
        };

        root.render(
            <React.StrictMode>
                <LabelLayoutDialog
                    labelProps={labelProperties}
                    pageProps={pageProperties}
                    onReturnLayout={handleReturnLayout}
                    onClose={handleClose}
                />
            </React.StrictMode>
        );
    },

    /**
     * Closes and unmounts the React component.
     */
    close: () =>
    {
        if (root)
        {
            root.unmount();
            root = null;
        }
        if (dialogContainer)
        {
            dialogContainer.remove();
            dialogContainer = null;
        }
    },
};

// Expose the API on the window object for legacy scripts
if (typeof window !== 'undefined')
{
    (window as any).LabelLayoutAPI = LabelLayoutAPI;
}

export default LabelLayoutAPI;